'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Service {
  id: string;
  title: string;
  description: string;
  features: string[];
  icon: string;
  metrics: {
    label: string;
    value: string;
  }[];
}

interface ServiceBreakdownProps {
  className?: string;
}

const ServiceBreakdown = ({ className = '' }: ServiceBreakdownProps) => {
  const [expandedService, setExpandedService] = useState<string>('sales-automation');

  const services: Service[] = [
    {
      id: 'sales-automation',
      title: 'Intelligent Sales Automation',
      description: 'Automate repetitive sales tasks and focus your team on high-value activities that close deals faster.',
      icon: 'BoltIcon',
      features: [
        'AI-powered lead scoring and prioritization',
        'Automated follow-up sequences with personalization',
        'Smart meeting scheduling and calendar optimization',
        'Intelligent email response suggestions',
        'Automated data entry and CRM updates',
        'Real-time sales activity tracking and alerts'
      ],
      metrics: [
        { label: 'Time Saved', value: '15 hrs/week' },
        { label: 'Lead Response', value: '10x faster' }
      ]
    },
    {
      id: 'pipeline-optimization',
      title: 'Pipeline Optimization & Management',
      description: 'Gain complete visibility into your sales pipeline with AI-driven insights that identify bottlenecks and opportunities.',
      icon: 'FunnelIcon',
      features: [
        'Real-time pipeline health monitoring',
        'AI-powered deal risk assessment',
        'Automated stage progression recommendations',
        'Predictive deal closure probability',
        'Bottleneck identification and resolution',
        'Custom pipeline workflow automation'
      ],
      metrics: [
        { label: 'Pipeline Velocity', value: '+45%' },
        { label: 'Deal Accuracy', value: '92%' }
      ]
    },
    {
      id: 'revenue-forecasting',
      title: 'Predictive Revenue Forecasting',
      description: 'Make data-driven decisions with AI-powered revenue predictions that adapt to market conditions and sales performance.',
      icon: 'ChartBarIcon',
      features: [
        'Machine learning revenue predictions',
        'Multi-scenario forecasting models',
        'Real-time forecast accuracy tracking',
        'Automated variance analysis and alerts',
        'Territory and rep-level forecasting',
        'Integration with financial planning systems'
      ],
      metrics: [
        { label: 'Forecast Accuracy', value: '95%' },
        { label: 'Planning Efficiency', value: '+60%' }
      ]
    }
  ];

  const toggleService = (serviceId: string) => {
    setExpandedService(expandedService === serviceId ? '' : serviceId);
  };

  return (
    <section className={`bg-background py-20 lg:py-32 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-accent/10 px-4 py-2 rounded-full mb-6">
            <Icon name="CogIcon" size={20} className="text-accent" />
            <span className="text-sm font-semibold text-accent">Comprehensive Solutions</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-6">
            Complete Revenue Growth Ecosystem
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Our integrated approach covers every aspect of revenue generation, from initial lead capture to predictive forecasting.
          </p>
        </div>

        {/* Service Cards */}
        <div className="space-y-6">
          {services.map((service) => {
            const isExpanded = expandedService === service.id;
            return (
              <div
                key={service.id}
                className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-elevated transition-all duration-300"
              >
                {/* Service Header */}
                <button
                  onClick={() => toggleService(service.id)}
                  className="w-full px-6 py-6 flex items-center justify-between hover:bg-muted/50 transition-colors duration-300"
                >
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                      <Icon name={service.icon as any} size={24} className="text-accent" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-xl font-heading font-bold text-foreground mb-1">
                        {service.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {service.description}
                      </p>
                    </div>
                  </div>
                  <Icon
                    name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                    size={24}
                    className="text-muted-foreground flex-shrink-0"
                  />
                </button>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="px-6 pb-6 border-t border-border">
                    <div className="grid lg:grid-cols-3 gap-8 pt-6">
                      {/* Features List */}
                      <div className="lg:col-span-2 space-y-3">
                        <h4 className="text-sm font-heading font-semibold text-foreground mb-4">
                          Key Features & Capabilities
                        </h4>
                        <div className="grid sm:grid-cols-2 gap-3">
                          {service.features.map((feature, index) => (
                            <div key={index} className="flex items-start space-x-3">
                              <Icon name="CheckCircleIcon" size={20} className="text-success flex-shrink-0 mt-0.5" />
                              <span className="text-sm text-foreground">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Metrics */}
                      <div className="space-y-4">
                        <h4 className="text-sm font-heading font-semibold text-foreground mb-4">
                          Expected Impact
                        </h4>
                        {service.metrics.map((metric, index) => (
                          <div key={index} className="bg-muted/50 rounded-lg p-4">
                            <div className="text-2xl font-heading font-bold text-accent mb-1">
                              {metric.value}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {metric.label}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServiceBreakdown;